import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useCurrencyHelpers } from '@plato/app-shared/hooks/use-currency-helpers';
import { SrInsightType } from '@plato/data/starrocks/types';
import { formatPercentage } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintTitle,
    InfoHintTrigger,
} from '@plato/ui/components/info-hint';
import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import {
    churnV2CardDimension,
    churnV2TooltipLength,
    hasChurnV2Tooltip,
} from '../components/churn-v2-dimension.ts';
import { getInsightName } from '../lib/insight-name.ts';
import { churnWarningPeriodArgs } from './churn-warning-periods.ts';

type InsightTranslations = {
    expanderClose: string;
    expanderOpen: string;
    prompt?: string;
};

type InsightTypeInfoHintProps = {
    insightData: InsightListItem;
};

export function InsightTypeInfoHint({ insightData }: InsightTypeInfoHintProps): ReactNode {
    const isLg = useBreakpoint('lg');
    const { t } = useTranslation();
    const { enableNeverBoughtCrossSellCopy } = useAppConfig();
    const insightTypeName = getInsightName(t, insightData.insight, {
        neverBoughtCrossSell: enableNeverBoughtCrossSellCopy,
    });
    const text = useInsightTranslations(insightData);

    return (
        <InfoHint delay={0}>
            <InfoHintTrigger />
            <InfoHintContent side={isLg ? 'right' : 'bottom'} className="max-w-[min(420px,calc(100vw-8px))]">
                <InfoHintHeader>
                    <InfoHintTitle>{insightTypeName}</InfoHintTitle>
                </InfoHintHeader>
                <InfoHintBody>{text.prompt}</InfoHintBody>
            </InfoHintContent>
        </InfoHint>
    );
}

function useInsightTranslations(insightItem: InsightListItem): InsightTranslations {
    const { t } = useTranslation('insights');
    const locale = useResolvedLocale();
    const { toCurrencyString } = useCurrencyHelpers();

    const { details, insight } = insightItem;
    const entityName = details.category?.title ?? insight.actionable_entity_id;

    if (insight.insight_type === SrInsightType.GLOBAL_CHURN_WARNING) {
        return {
            expanderClose: t(['expander_show_less_churn_warning', 'expander_show_less']),
            expanderOpen: t('expander_show_more_churn_warning'),
            prompt: t('prompt__global_churn_warning'),
        };
    }
    if (insight.insight_type === SrInsightType.CHURN_WARNING) {
        const toggleOptions = {
            expanderClose: t(['expander_show_less_churn_warning', 'expander_show_less']),
            expanderOpen: t('expander_show_more_churn_warning'),
        };

        const args = insight.template_arguments;
        if (args) {
            const dim = churnV2CardDimension(args);
            if (hasChurnV2Tooltip(dim)) {
                const periodArgs = churnWarningPeriodArgs(churnV2TooltipLength(dim, args), locale);
                // Only the five dimensions hasChurnV2Tooltip covers reach this switch; the rest
                // fall through to the generic prompt below.
                switch (dim) {
                    case 'd2_cadence':
                        return {
                            ...toggleOptions,
                            prompt: t('churn_v2_tooltip__d2_cadence', { ns: 'insights', ...periodArgs }),
                        };
                    case 'd3_annual':
                        return {
                            ...toggleOptions,
                            prompt: t('churn_v2_tooltip__d3_annual', { ns: 'insights', ...periodArgs }),
                        };
                    case 'd4_seasonal':
                        return {
                            ...toggleOptions,
                            prompt: t('churn_v2_tooltip__d4_seasonal', { ns: 'insights', ...periodArgs }),
                        };
                    case 'd5_revenue':
                        return {
                            ...toggleOptions,
                            prompt: t('churn_v2_tooltip__d5_revenue', { ns: 'insights', ...periodArgs }),
                        };
                    case 'd6_momentum':
                        return {
                            ...toggleOptions,
                            prompt: t('churn_v2_tooltip__d6_momentum', { ns: 'insights', ...periodArgs }),
                        };
                }
            }
        }

        return {
            ...toggleOptions,
            prompt: t('prompt__churn_warning'),
        };
    }
    if (insight.insight_type === SrInsightType.CROSS_SELL) {
        return {
            expanderClose: t(['expander_show_less_cross_sell', 'expander_show_less']),
            expanderOpen: t(['expander_show_more_cross_sell', 'expander_show_more']),
            prompt: t('prompt__cross_sell'),
        };
    }
    if (insight.insight_type === SrInsightType.FIRST_PURCHASE) {
        return {
            expanderClose: t(['expander_show_less_first_purchase', 'expander_show_less']),
            expanderOpen: t(['expander_show_more_first_purchase', 'expander_show_more']),
            prompt: t('prompt__first_purchase'),
        };
    }
    if (insight.insight_type === SrInsightType.SHARE_OF_WALLET) {
        return {
            expanderClose: t(['expander_show_less_share_of_wallet', 'expander_show_less']),
            expanderOpen: t(['expander_show_more_share_of_wallet', 'expander_show_more']),
            prompt: t('prompt__share_of_wallet'),
        };
    }
    if (insight.insight_type === SrInsightType.PREVIOUSLY_PURCHASED) {
        const toggleOptions = {
            expanderClose: t(['expander_toggle_fallback', 'expander_show_less']),
            expanderOpen: t(['expander_toggle_fallback', 'expander_show_more']),
        };

        const args = insight.template_arguments;
        if (!args) {
            return toggleOptions;
        }

        return {
            ...toggleOptions,
            prompt: t('prompt__previously_purchased', {
                revenue_potential: toCurrencyString(args.revenue_potential),
                actionable_entity_name: args.actionable_entity_name || entityName,
            }),
        };
    }
    if (insight.insight_type === SrInsightType.GLOBAL_TOP) {
        const toggleOptions = {
            expanderClose: t(['expander_toggle_fallback', 'expander_show_less']),
            expanderOpen: t(['expander_toggle_fallback', 'expander_show_more']),
        };

        const args = insight.template_arguments;
        if (!args) {
            return toggleOptions;
        }

        return {
            ...toggleOptions,
            prompt: t('prompt__global_top', {
                actionable_entity_name: args.actionable_entity_name || entityName,
                penetration_percentage_formatted: formatPercentage(
                    args.penetration_percentage_formatted / 100,
                    { locale },
                ),
            }),
        };
    }

    const toggleOptions = {
        expanderClose: t(['expander_toggle_fallback', 'expander_show_less']),
        expanderOpen: t(['expander_toggle_fallback', 'expander_show_more']),
    };

    const args = insight.template_arguments;
    if (!args) {
        return toggleOptions;
    }

    return {
        ...toggleOptions,
        prompt: t('prompt__industry_top', {
            industry_sector: args.industry_sector,
            actionable_entity_name: args.actionable_entity_name || entityName,
            penetration_percentage_formatted: formatPercentage(args.penetration_percentage_formatted / 100, {
                locale,
            }),
        }),
    };
}
